import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ratio, percent, CheckboxDropdown, pitchTypeDisplayName, pitchSubTypeColor } from 'yankees-react-utils';
import StrikeZone from '../../../../components/StrikeZone';
import ReportCard from '../../../../components/ReportCard';

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-menu {
        width: 300px;
    }
`;

const getSeries = (pitches, repertoire, translate) => {
    const categories = pitches.filter((p) => p.fxtype !== 1).reduce((acc, p) => {
        if (!acc[`${p.fxtype}|${p.fxsubtype}`]) acc[`${p.fxtype}|${p.fxsubtype}`] = [];
        acc[`${p.fxtype}|${p.fxsubtype}`].push(p);
        if (p.fxtype === 1) return acc;

        const count = `${p.balls}-${p.strikes}`;
        if (acc[count]) acc[count].push(p);
        if (p.strikes < p.balls) acc[translate('sections.opponent.player.hitter-ahead') || 'Hitter Ahead'].push(p);
        if (p.strikes === 2) acc[translate('sections.opponent.player.two-strikes') || '2 Strikes'].push(p);
        if (p.risp) acc[translate('baseball.abbr-runners-in-position') || 'RISP'].push(p);
        return acc;
    }, {
        '0-0': [],
        '1-1': [],
        '3-2': [],
        [translate('sections.opponent.player.two-strikes') || '2 Strikes']: [],
        [translate('sections.opponent.player.hitter-ahead') || 'Hitter Ahead']: [],
        [translate('baseball.abbr-runners-in-position') || 'RISP']: [],
    });
    const series = Object.keys(categories).reduce((acc, category) => {
        const pitchTypeAbbr = category.includes('|')
        && (pitchTypeDisplayName({
            fxtype: category.split('|')[0],
            fxsubtype: category.split('|')[1] > 0 ? category.split('|')[1] : null,
        }, true, true, translate));
        const name = `${pitchTypeAbbr ? `${pitchTypeAbbr} ${translate('sections.opponent.player.pitch-location')}` : category}`;
        const pitchTypeSeries = categories[category].reduce((pitchTypeAcc, p, _idx, self) => {
            const color = pitchSubTypeColor(p);
            if (!pitchTypeAcc[`${p.fxtype}|${p.fxsubtype}`]) {
                const statIdx = repertoire.findIndex((r) => r.fxtype === p.fxtype && r.fxsubtype === p.fxsubtype);
                let seriesName = pitchTypeDisplayName(p, true, true, translate);
                if (!pitchTypeAbbr) {
                    let perc;
                    const stat = statIdx >= 0 ? repertoire[statIdx] : null;
                    if (stat) {
                        if (category === '0-0') perc = percent(ratio(stat.firstpit, stat.firstpit_total), 0);
                        else if (category === '3-2') perc = percent(ratio(stat.cnt32, stat.cnt32_total), 0);
                        else if (category === (translate('sections.opponent.player.two-strikes') || '2 Strikes')) {
                            perc = percent(ratio(stat.twostrikes, stat.twostrikes_total), 0);
                        } else if (category === (translate('sections.opponent.player.hitter-ahead') || 'Hitter Ahead')) {
                            perc = percent(ratio(stat.ahead, stat.ahead_total), 0);
                        } else if (category === (translate('baseball.abbr-runners-in-position') || 'RISP')) {
                            perc = percent(ratio(stat.risp, stat.risp_total), 0);
                        }
                    }
                    if (!perc) {
                        const numPitchType = self.filter((x) => x.fxtype === p.fxtype && x.fxsubtype === p.fxsubtype).length;
                        perc = percent(ratio(numPitchType, self.length), 0);
                    }
                    seriesName += ` ${perc}`;
                }
                pitchTypeAcc[`${p.fxtype}|${p.fxsubtype}`] = {
                    id: `${name}|${`${p.fxtype}|${p.fxsubtype}`}`,
                    name: seriesName,
                    showlegend: !pitchTypeAbbr,
                    sortOrder: pitchTypeAbbr ? statIdx + 10 : ['0-0', '1-1', translate('sections.opponent.player.hitter-ahead') || 'Hitter Ahead',
                        translate('sections.opponent.player.two-strikes') || '2 Strikes', '3-2',
                        translate('baseball.abbr-runners-in-position') || 'RISP'].indexOf(category),
                    color,
                    fxtype: p.fxtype,
                    fxsubtype: p.fxsubtype,
                    data: [],
                };
            }
            if (pitchTypeAcc[`${p.fxtype}|${p.fxsubtype}`]) {
                pitchTypeAcc[`${p.fxtype}|${p.fxsubtype}`].data.push({
                    x: -p.platex,
                    y: p.platez,
                    name: '',
                    gameid: p.gameid,
                    eventseq: p.eventseq,
                    pitchseq: p.pitchseq,
                    marker: { symbol: p.stk ? 'circle' : 'square', radius: 2.5 },
                    color,
                });
            }
            return pitchTypeAcc;
        }, {});
        acc[name] = Object.values(pitchTypeSeries)
            .sort((a, b) => repertoire.findIndex((r) => r.fxtype === a.fxtype && r.fxsubtype === a.fxsubtype)
            - repertoire.findIndex((r) => r.fxtype === b.fxtype && r.fxsubtype === b.fxsubtype));
        return acc;
    }, {});
    return series;
};

const getDefaultSelectedSeries = (series, translate) => Object.keys(series)
    .sort((a, b) => {
        if (series[a][0] == null || series[b][0] == null) return 1;
        if (series[a][0].sortOrder === series[b][0].sortOrder) return a.localeCompare(b);
        return series[a][0].sortOrder - series[b][0].sortOrder;
    })
    .map((s) => ({
        name: s,
        checked: ['0-0', translate('sections.opponent.player.hitter-ahead') || 'Hitter Ahead',
            translate('sections.opponent.player.two-strikes') || '2 Strikes', '3-2'].some((x) => s.includes(x)),
    }));

const PitchLocations = ({ levelId, isLhb, isLhp, pitches, repertoire, yearId }) => {
    const { t: translate } = useTranslation();

    const series = getSeries(pitches, repertoire, translate);
    const [selectedSeries, setSelectedSeries] = useState(getDefaultSelectedSeries(series, translate));

    useEffect(() => {
        setSelectedSeries(getDefaultSelectedSeries(series, translate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [repertoire]);

    return (
        <ReportCard title={translate('sections.reports.pitcher.pitch-locations')}>
            {pitches.length ? (
                <>
                    <div className="d-flex justify-content-center d-print-none mb-1">
                        <StyledCheckboxDropdown
                            value={selectedSeries}
                            onChange={(o) => {
                                setSelectedSeries(selectedSeries.map((s) => ({ ...s, checked: s.name === o.name ? !s.checked : s.checked })));
                            }}
                            options={selectedSeries}
                            titleOverrideFormat
                            title={`${selectedSeries.filter((s) => s.checked).length} ${translate('common.selected')}`}
                        />
                    </div>
                    <div className="d-flex flex-wrap justify-content-around">
                        {selectedSeries.filter((s) => s.checked).map((s) => (
                            <div key={s.name}>
                                <div className="fs-6 fw-bold mb-2 text-center">{s.name}</div>
                                <StrikeZone
                                    szcontour={{
                                        yearid: yearId,
                                        levelid: levelId,
                                        islhb: isLhb,
                                        isLhp,
                                    }}
                                    seriesData={series[s.name] ?? []}
                                    showlegend={series[s.name] ? series[s.name][0]?.showlegend : false}
                                    legendposition="below"
                                    showlhb={isLhb}
                                    height={192 + (17 * (Math.ceil(repertoire.length / 2)))}
                                    size="ocsheet"
                                    yopposite
                                    showoffgrid
                                    pitcherView={false}
                                    customLegend={{
                                        symbolPadding: 2,
                                        padding: 8,
                                        margin: 4,
                                        itemWidth: 105,
                                        itemDistance: 2,
                                        itemStyle: { fontSize: '9px', fontWeight: 500 },
                                        align: 'center',
                                        width: 210,
                                        y: 10,
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </>
            ) : <div className="d-flex justify-content-center py-5 text-muted">{translate('pitcher-overview.no-data')}</div>}
        </ReportCard>
    );
};

export default PitchLocations;
